import { Children, cloneElement, ReactElement, useRef } from 'react';
import { Card, CardProps, Checkbox, CheckboxRef, Col, Form, FormItemProps, Row, Space, theme, Typography } from 'antd';

export type OptionCardsProps = {
  items?: OptionCardProps[];
  label?: string;
};

export const OptionCards = ({ items, label }: OptionCardsProps) => {
  return (
    <>
      {label && (
        <Row>
          <Typography.Text style={{ marginBottom: 10 }}>{label}</Typography.Text>
        </Row>
      )}
      {items && (
        <Row gutter={[20, 20]} style={{ marginBottom: 30 }}>
          {items.map((optionCard, index) => {
            return (
              <Col key={(optionCard.children as ReactElement)?.props?.label ?? optionCard.key ?? index} span={6}>
                <OptionCard {...optionCard} />
              </Col>
            );
          })}
        </Row>
      )}
    </>
  );
};

export type OptionCardProps = CardProps & {
  key?: React.Key;
  checkedHelp?: string;
  uncheckedHelp?: string;
};

export const OptionCard = ({ checkedHelp, children, uncheckedHelp, ...props }: OptionCardProps) => {
  const { token } = theme.useToken();
  const form = Form.useFormInstance();
  const checkboxRef = useRef<CheckboxRef>(null);

  const formItems =
    (Children.toArray(children).filter((child) => (child as ReactElement)?.type === Form.Item) as ReactElement[]) ?? [];

  const value: boolean = Form.useWatch(formItems[0]?.props.name);

  if (formItems.length !== 1) {
    throw new Error('Option cards should have exactly one form item.');
  }

  return (
    <Card
      {...props}
      size="small"
      style={{
        backgroundColor: value ? token.colorInfoBg : undefined,
        borderColor: value ? token.colorInfoBorder : undefined,
      }}
      onClick={() => {
        form.setFieldValue(formItems[0].props.name, !value);
        checkboxRef.current?.focus();
      }}
    >
      {Children.map(children, (_child) => {
        const child = _child as ReactElement;

        if (child.type === Form.Item && child.props.name === formItems[0].props.name) {
          const grandchild: ReactElement = child.props.children;

          return cloneElement<FormItemProps>(child, {
            ...child.props,
            children:
              child.props.children === undefined || child.props.children === null ? (
                <Checkbox ref={checkboxRef} />
              ) : (
                cloneElement(grandchild, { ...grandchild.props, ref: checkboxRef })
              ),
            label: (
              <Typography.Text
                style={{ color: value ? token.colorInfoText : undefined }}
                onClick={(e) => e.stopPropagation()}
              >
                {child.props.label}
              </Typography.Text>
            ),
            help:
              child.props.help || (value && checkedHelp) || (!value && uncheckedHelp) ? (
                <Space direction="vertical">
                  <Typography.Text style={{ color: value ? token.colorText : token.colorTextDescription }}>
                    {value ? checkedHelp : uncheckedHelp}
                  </Typography.Text>
                  {child.props.help}
                </Space>
              ) : undefined,
            valuePropName: 'checked',
          });
        }

        return child;
      })}
    </Card>
  );
};
